// src/storage/db.ts
// ローカル永続化層 (IndexedDB / Dexie.js) — 6.1節のスキーマ定義。
//
// 設計判断: 高頻度の時系列データ(60Hz)を個別レコードで持つとIndexedDBの
// 性能を圧迫するため、圧縮BLOBとして1レコードに集約する(FrameMetric.timeSeries)。
//
// Web特有の注意点: ブラウザのストレージは navigator.storage.persist() で
// ユーザーに永続化を明示許可してもらわない限り、端末のストレージ逼迫時に
// ブラウザ側が予告なくIndexedDBのデータを削除する可能性がある(9章参照)。

import Dexie, { type Table } from "dexie";

export type ExerciseType = "DEADLIFT" | "SQUAT";
export type RiskLevel = "safe" | "caution" | "danger";

export interface Session {
  id: string; // UUID (PK)
  exerciseType: ExerciseType;
  startedAt: number;
  endedAt: number;
  frameCount: number;
  deviceInfo: string; // UA文字列の要約(8章のブラウザ対応表分析用)
  isSynced: boolean;
}

export interface FrameMetric {
  sessionId: string; // FK
  timeSeries: Blob; // CBOR圧縮済み: L4/L5値・関節角度の60Hz系列
}

export interface ResultSummary {
  sessionId: string; // FK
  overallScore: number;
  maxShearForce: number;
  maxCompressionForce: number;
  riskLevel: RiskLevel;
  slmAdviceText: string;
  pdfExportedAt?: number;
}

class KinemaDB extends Dexie {
  sessions!: Table<Session, string>;
  frameMetrics!: Table<FrameMetric, string>;
  resultSummaries!: Table<ResultSummary, string>;

  constructor() {
    super("KinemaLensDB");
    this.version(1).stores({
      sessions: "id, exerciseType, startedAt",
      frameMetrics: "sessionId",
      resultSummaries: "sessionId, riskLevel",
    });
  }
}

export const db = new KinemaDB();

/**
 * ブラウザに永続化ストレージを要求する(9章: ストレージ容量逼迫による自動削除への対策)。
 * 対応ブラウザでは、ユーザーの操作(インストール・ブックマーク・頻繁な訪問など)を
 * トリガーに許可が下りることが多い。非対応ブラウザではno-opで安全にフォールバックする。
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!("storage" in navigator) || !("persist" in navigator.storage)) {
    return false;
  }
  try {
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

/** 残容量を監視し、逼迫時にUI側で警告バナーを出すためのヘルパー(9章)。 */
export async function getStorageEstimate(): Promise<{
  usageRatio: number | null;
}> {
  if (!("storage" in navigator) || !("estimate" in navigator.storage)) {
    return { usageRatio: null };
  }
  try {
    const { usage, quota } = await navigator.storage.estimate();
    if (!usage || !quota) return { usageRatio: null };
    return { usageRatio: usage / quota };
  } catch {
    return { usageRatio: null };
  }
}

/** 10.3節: 設定画面からのデータ削除リクエスト(GDPR/APPI対応)を即時実行する。 */
export async function deleteAllLocalData(): Promise<void> {
  await db.transaction(
    "rw",
    db.sessions,
    db.frameMetrics,
    db.resultSummaries,
    async () => {
      await db.sessions.clear();
      await db.frameMetrics.clear();
      await db.resultSummaries.clear();
    },
  );
}

export async function listRecentSessions(limit = 20) {
  return db.sessions.orderBy("startedAt").reverse().limit(limit).toArray();
}

export async function getResultSummary(sessionId: string) {
  return db.resultSummaries.get(sessionId);
}
